import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Stack, Text,
    Textarea,
    useColorModeValue
} from "@chakra-ui/react";
import {checkErrors, hasError, parseStringToDateTime} from "../../../utils/forms-utils";
import { processesService } from "../../../services/processesService";
import Card from "../../../components/Card/Card";
import ListPageCardHeader from "../../../components/Card/ListPageCardHeader";
import CardBody from "../../../components/Card/CardBody";
import Loading from "../../../components/Loading/Loading";
import SearchSelect from "../../../components/SearchSelect/SearchSelect";
import { FileAttachmentInput } from "../../../components/FileAttachmentInput/FileAttachmentInput";
import { ROUTES } from "../../../constants/routes-path";
import { userService } from "../../../services/userService";
import { toServerDate } from "../../../utils/traffic-violation-utils";
import DateTime from "react-datetime";
import moment from "moment";
import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR } from "constants/date-time";

export function ProcessReexamination() {
    const bgTextarea = useColorModeValue("white", "navy.900");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");
    const cardSpacing = "20px";

    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const attachmentRef = useRef(undefined);
    const [formData, setFormData] = useState(undefined);
    const [fileAttachmentName, setAttachmentFileName] = useState(null);
    const [inputError, setInputError] = useState({});
    const [persons, setPersons] = useState([]);
    const [processData, setProcessData] = useState(undefined);
    const translationKey = "process";

    const isFormInvalid = () => formData && inputError ? hasError({ formData, inputError }) : true;

    const ids = useMemo(() => ({
        user: "user",
        observations: "observations",
        dataLimiteJulgamento: "data-limite-julgamento",
        file: "file",
        documentFileInfo: "fileInfo",
    }), []);

    const initFormData = (processRouting) => ({
        [ids.user]: {
            value: processRouting?.usuario_responsavel_atual?.id_pessoa || "",
            validations: [{ required: true }],
        },
        [ids.observations]: {
            value: processRouting?.observacoes || "",
            validations: [],
        },
        [ids.dataLimiteJulgamento]: {
            value: "",
            validations: [{ required: true }],
        },
        [ids.file]: {
            value: null,
            validations: [],
        }
    });

    useEffect(() => {
        if (id) {
            fetchProcess();
        }
    }, [id]);

    const latestProcessRouting = (routingList = []) => {
        if (!routingList || routingList.length === 0) return null;
        return routingList
            .slice()
            .sort((a, b) => parseStringToDateTime(b.data_criacao).getTime() - parseStringToDateTime(a.data_criacao).getTime())[0];
    };

    const fetchProcess = async () => {
        try {
            const process = await processesService.find(id);
            const response = await userService.findMany();
            const userList = response?._embedded?.usuario_list || [];
            setProcessData(process);
            const users = userList.map(user => ({
                ...user,
                pessoa_id_: user.pessoa?.id,
                username: user.pessoa?.nome || user.login,
            }));
            setPersons(users);
            const processRouting = latestProcessRouting(process.tramitacoes_processos)
            setFormData(initFormData(processRouting));
        } catch (error) {
            showToast({
                title: t('process.messages.errors.list'),
                status: "error"
            });
            redirectToList();
        }
    }

    const handleInputChange = (e) => {
        const id = e.target.id;
        const value = e.target.value;

        const newFormData = { ...formData, [id]: { ...formData[id], value } };
        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
    };

    function toDto() {
        const personSelected = persons.find(value => value?.pessoa?.id === formData[ids.user].value)
        return {
            ...processData,
            id: id,
            usuario_responsavel_atual: { id: personSelected.id },
            observacoes: formData[ids.observations].value,
            data_limite_julgamento: toServerDate(formData[ids.dataLimiteJulgamento].value),
        };
    }

    const handleJudgmentLimitDateChange = (value) => {
        const m = moment(value);
        const date = m.isValid() ? m.format(DATE_FORMAT_ISO) : "";

        const updated = {
            ...formData,
            [ids.dataLimiteJulgamento]: {
                ...formData[ids.dataLimiteJulgamento],
                value: date,
            },
        };

        setFormData(updated);

        setInputError(prev =>
            checkErrors({
                formData: updated,
                inputError: prev,
                name: ids.dataLimiteJulgamento
            })
        );
    };

    const handleSubmit = async () => {
        if (!attachmentRef.current?.isValid()) {
            attachmentRef.current?.markTouched();
            return;
        }
        const fileWrapper = attachmentRef?.current?.getFile?.();
        const file = fileWrapper?.file ?? null;

        const processDTO = toDto();

        const form = new FormData();
        form.append("requestData", new Blob([JSON.stringify(processDTO)], { type: 'application/json' }));
        if (file) {
            form.append("anexo", file);
        }

        try {
            await processesService.reexamine(id, form);
            showToast({
                title: t("process.process-reexamination.messages.success.reexamination"),
                status: "success"
            });
            redirectToList();
        } catch (error) {
            showToast({
                title: t(error),
                status: "error"
            });
        }
    };

    const redirectToList = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.processes);
    };

    return (
        <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "75px" }}>
            <Card mt={"80px"}>
                <ListPageCardHeader
                    title={t('process.process-reexamination.form.title')}
                    subtitle={t('process.process-reexamination.form.subtitle') + processData?.numero}
                />
                <CardBody px="22px">
                    {!!formData ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            {/*ait-agent*/}
                            <FormControl isRequired isInvalid={inputError[ids.user]}>
                                <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                                    {t(`${translationKey}.process-reexamination.form.user-responsable`)}
                                </FormLabel>
                                <SearchSelect
                                    required
                                    valueField="pessoa_id_"
                                    id={ids.user}
                                    value={formData[ids.user].value}
                                    onChange={handleInputChange}
                                    isDisabled={!persons.length}
                                    placeholder={t(`${translationKey}.process-reexamination.form.user-responsable-placeholder`)}
                                    displayField={["username"]}
                                    items={persons}
                                />
                                <FormErrorMessage>
                                    {t(inputError[ids.user])}
                                </FormErrorMessage>
                            </FormControl>

                            {/*data_limite_julgamento*/}
                            <FormControl isRequired isInvalid={inputError[ids.dataLimiteJulgamento]}>
                                <FormLabel fontSize="xs" fontWeight="bold">
                                    {t(`${translationKey}.process-reexamination.form.judgment-limit-date`)}
                                </FormLabel>
                                <DateTime
                                    dateFormat={DATE_FORMAT}
                                    timeFormat={false}
                                    locale={LOCALE_PT_BR}
                                    value={
                                        formData[ids.dataLimiteJulgamento].value
                                            ? moment(formData[ids.dataLimiteJulgamento].value, DATE_FORMAT_ISO)
                                            : ""
                                    }
                                    renderInput={(props) => (
                                        <Input
                                            {...props}
                                            id={ids.dataLimiteJulgamento}
                                            fontSize="sm"
                                            variant="main"
                                            placeholder={t(`${translationKey}.process-reexamination.form.judgment-limit-date-placeholder`)}
                                        />
                                    )}
                                    onChange={handleJudgmentLimitDateChange}
                                />
                                <FormErrorMessage>
                                    {t(inputError[ids.dataLimiteJulgamento])}
                                </FormErrorMessage>
                            </FormControl>

                            {/*anexo*/}
                            <FileAttachmentInput
                                isRequired={true}
                                ref={attachmentRef}
                                id={ids.file}
                                nameVariable={fileAttachmentName}
                                acceptedTypes={['application/pdf', 'image/jpeg']}
                                translationKey={translationKey + '.form'}
                            />

                            {/*observacao*/}
                            <FormControl isInvalid={inputError[ids.observations]}>
                                <FormLabel fontSize="xs" fontWeight="bold">
                                    {t("documents.form.fields.observations.label")}
                                </FormLabel>
                                <Textarea
                                    minH="120px"
                                    bg={bgTextarea}
                                    id={ids.observations}
                                    value={formData[ids.observations].value}
                                    onChange={handleInputChange}
                                    _placeholder={{ color: placeholderColor }}
                                    placeholder={t('documents.form.fields.observations.placeholder')}
                                    fontSize="xs"
                                />
                                <FormErrorMessage>
                                    {t(inputError[ids.observations])}
                                </FormErrorMessage>
                            </FormControl>

                            <Stack w="100%" direction="row" justify="space-between">
                                <Button w="125px"
                                        variant="no-effects"
                                        bg="gray.100"
                                        color="gray.700"
                                        fontSize="sm"
                                        fontWeight="bold"
                                        onClick={redirectToList}
                                >
                                    <Text>
                                        {t("system.actions.cancel")}
                                    </Text>
                                </Button>

                                <Button
                                    w="125px"
                                    variant="dark"
                                    fontSize="sm"
                                    isDisabled={isFormInvalid()}
                                    onClick={handleSubmit}
                                    _hover={isFormInvalid() ? {} : undefined}
                                >
                                    <Text>
                                        {t("system.actions.save")}
                                    </Text>
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
        </Flex>
    )
}
